<template>
  <div>
    <ReportItemContainer v-if="isReportMode">
      <template #question>{{ question.text }}</template>
      <template #answer>
        <div
          v-if="showAnswer"
          v-for="(company, index) in companies"
          class="mb-3"
          :key="index"
        >
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("companyName") }}: </span
            >{{ company.companyName }}
          </p>
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("companyDateOfIncorporation") }}: </span
            >{{ company.dateOfIncorporation }}
          </p>
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("companyCountryOfIncorporation") }}: </span
            >{{ company.countryOfIncorporation }}
          </p>
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("companyBusinessRegistrationNumber") }}: </span
            >{{ company.businessRegistrationNumber }}
          </p>
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("companyOrganizationalForm") }}: </span
            >{{ company.organizationalForm }}
          </p>
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("companyIndustry") }}: </span
            >{{ company.industry }}
          </p>
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("companyWebsite") }}: </span
            >{{ company.website }}
          </p>
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("address") }}: </span
            >{{ company.address.street }}
          </p>
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("city") }}: </span
            >{{ company.address.city }}
          </p>
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("region") }}: </span
            >{{ company.address.region }}
          </p>
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("country") }}: </span
            >{{ company.address.country }}
          </p>
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("companySubjectToSanctions") }}: </span
            >{{ company.subjectToSanctions }}
          </p>
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("beneficialOwnership") }}: </span
            >{{ company.knownBeneficialOwner }}
          </p>
        </div>
      </template>
    </ReportItemContainer>

    <div v-else>
      <v-row>
        <v-col cols="12" sm="6" md="12">
          <h3 class="mb-5">
            {{ question.text }}
            <InfoPopup
              v-if="question.helpText"
              :title="question.helpTitle ?? ''"
              :content="question.helpText"
            />
          </h3>
        </v-col>
      </v-row>
      <div v-for="(company, index) in companies" class="mb-8" :key="index">
        <v-divider v-if="index !== 0" class="mb-5"></v-divider>
        <v-row>
          <v-col cols="12" sm="6" md="6">
            <PInputField
              :label="getLabel('companyName')"
              :value="company.companyName"
              :readonly="isReadOnlyMode"
              @update-event="
                (val) => {
                  company.companyName = val;
                  emitNewCompany();
                }
              "
            />
          </v-col>
          <v-col cols="12" sm="6" md="6">
            <p>{{ getLabel("companyDateOfIncorporation") }}</p>
            <DateInput
              :value="company.dateOfIncorporation"
              :is-disabled="isReadOnlyMode"
              @update-event="
                (val) => {
                  company.dateOfIncorporation = val;
                  emitNewCompany();
                }
              "
              @valid="(isValid) => emitValid(isValid, 'dateOfIncorporation')"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6" md="6">
            <p>{{ getLabel("companyCountryOfIncorporation") }}</p>
            <PAutocomplete
              :model-value="company.countryOfIncorporation"
              :items="itemsStore.countryNames"
              :select-text="company.countryOfIncorporation"
              :disabled="isReadOnlyMode"
              :placeholder="getLabel('companySelectCountryPlaceHolder')"
              @field-updated="(val: string) => { (company.countryOfIncorporation = val); emitNewCompany() }"
            />
          </v-col>
          <v-col cols="12" sm="6" md="6">
            <PInputField
              :label="getLabel('companyBusinessRegistrationNumber')"
              :value="company.businessRegistrationNumber"
              :readonly="isReadOnlyMode"
              @update-event="
                (val) => {
                  company.businessRegistrationNumber = val;
                  emitNewCompany();
                }
              "
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6" md="6">
            <p>{{ getLabel("companyOrganizationalForm") }}</p>
            <PAutocomplete
              :model-value="company.organizationalForm"
              :items="organisationForms"
              :select-text="company.organizationalForm"
              :disabled="isReadOnlyMode"
              :placeholder="getLabel('companyOrganizationalFormPlaceHolder')"
              @field-updated="(val: string) => { (company.organizationalForm = val); emitNewCompany() }"
            />
          </v-col>
          <v-col cols="12" sm="6" md="6">
            <p>{{ getLabel("companyIndustry") }}</p>
            <PAutocomplete
              :model-value="company.industry"
              :items="industries"
              :select-text="company.industry"
              :disabled="isReadOnlyMode"
              :placeholder="getLabel('companyIndustryPlaceHolder')"
              @field-updated="(val: string) => { (company.industry = val); emitNewCompany() }"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6" md="6">
            <PInputField
              :label="getLabel('companyWebsite')"
              :value="company.website"
              :readonly="isReadOnlyMode"
              @update-event="
                (val) => {
                  company.website = val;
                  emitNewCompany();
                }
              "
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6" md="6">
            <PInputField
              :label="getLabel('address')"
              :value="company.address.street"
              :readonly="isReadOnlyMode"
              @update-event="
                (val) => {
                  company.address.street = val;
                  emitNewCompany();
                }
              "
            />
          </v-col>
          <v-col cols="12" sm="6" md="6">
            <PInputField
              :label="getLabel('city')"
              :value="company.address.city"
              :readonly="isReadOnlyMode"
              @update-event="
                (val) => {
                  company.address.city = val;
                  emitNewCompany();
                }
              "
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6" md="6">
            <PInputField
              :label="getLabel('region')"
              :value="company.address.region"
              :readonly="isReadOnlyMode"
              @update-event="
                (val) => {
                  company.address.region = val;
                  emitNewCompany();
                }
              "
            />
          </v-col>
          <v-col cols="12" sm="6" md="6">
            <p>{{ getLabel("country") }}</p>
            <PAutocomplete
              :model-value="company.address.country"
              :items="itemsStore.countryNames"
              :select-text="company.address.country"
              :disabled="isReadOnlyMode"
              @field-updated="(val: string) => { (company.address.country = val); emitNewCompany() }"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6" md="6">
            <div class="d-flex align-center">
              <p class="mb-4">{{ getLabel("companySubjectToSanctions") }}</p>
            </div>
            <PCheckBox
              :label="getBooleanDisplayText('yes')"
              :value="company.subjectToSanctions === StringBoolean.Yes"
              :disabled="isReadOnlyMode"
              @value-changed="
                () => {
                  company.subjectToSanctions = StringBoolean.Yes;
                  emitNewCompany();
                }
              "
            />
            <PCheckBox
              class="mt-3"
              :label="getBooleanDisplayText('no')"
              :value="company.subjectToSanctions === StringBoolean.No"
              :disabled="isReadOnlyMode"
              @value-changed="
                () => {
                  company.subjectToSanctions = StringBoolean.No;
                  emitNewCompany();
                }
              "
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6" md="6">
            <div class="d-flex align-center">
              <p class="mb-4">{{ getLabel("beneficialOwnership") }}</p>
              <InfoPopup
                :title="getLabel('beneficialOwnershipHelpTitle')"
                :content="getLabel('beneficialOwnershipHelpText')"
              />
            </div>

            <PCheckBox
              :label="getBooleanDisplayText('yes')"
              :value="company.knownBeneficialOwner === StringBoolean.Yes"
              :disabled="isReadOnlyMode"
              @value-changed="
                (_) => {
                  company.knownBeneficialOwner = StringBoolean.Yes;
                  emitNewCompany();
                }
              "
            />
            <PCheckBox
              class="mt-3"
              :label="getBooleanDisplayText('no')"
              :value="company.knownBeneficialOwner === StringBoolean.No"
              :disabled="isReadOnlyMode"
              @value-changed="
                (_) => {
                  company.knownBeneficialOwner = StringBoolean.No;
                  emitNewCompany();
                }
              "
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col class="pl-0">
            <v-btn
              size="medium"
              variant="plain"
              :disabled="isReadOnlyMode"
              @click="addCompany()"
              class="ma-2 mb-15"
              v-if="index == companies.length - 1"
            >
              <v-icon icon="mdi-plus"></v-icon>
              <u class="mb-1">{{ getLabel("addCompany") }}</u>
            </v-btn>
          </v-col>
          <v-col class="text-right">
            <v-btn
              size="medium"
              variant="plain"
              @click="removeCompany(company)"
              class="ma-2 mb-15"
              v-if="companies.length > 1"
            >
              <v-icon icon="mdi-delete"></v-icon>
              <u class="mb-1">{{ getLabel("removeCompany") }}</u>
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, type Ref } from "vue";
import ReportItemContainer from "@/components/containers/ReportItemContainer.vue";
import type { QuestionModelDto } from "@/models/dto/questionModelDto";
import PAutocomplete from "@/components/custom/PAutocomplete.vue";
import InfoPopup from "@/components/custom/InfoPopup.vue";
import PInputField from "@/components/custom/PInputField.vue";
import { useItemsStore } from "@/stores/itemsStore";
import PCheckBox from "@/components/custom/PCheckBox.vue";
import { QuestionnaireTemplateCommonItemsId } from "@/models/enum/questionnaireTemplateCommonItemsId";
import { useSelectedQuestionnaire } from "@/stores/selectedQuestionnare";
import { StringBoolean } from "@/models/enum/stringBoolean";
import { companyOwnershipAnswerComplete } from "@/components/questionnaire/questionnaireUtils";
import type {
  AnswerCompanyOwner,
  CompanyOwner,
} from "@/models/questions/companyOwner";
import { createEmptyCompanyOwner } from "@/models/questions/companyOwner";
import DateInput from "@/components/custom/DateInput.vue";
import { industries } from "@/modules/industries";
import { organisationForms } from "@/modules/organisationForms";

const props = withDefaults(
  defineProps<{
    questionModel: QuestionModelDto;
    answer?: AnswerCompanyOwner[] | null;
    isReportMode?: boolean;
    isReadOnlyMode?: boolean;
  }>(),
  {
    isReportMode: false,
    isReadOnlyMode: false,
  }
);

const emit = defineEmits<{
  (e: "qEvent", newValue: AnswerCompanyOwner[]): void;
  (e: "isValid", isValid: boolean, invalidField: string): void;
}>();

const selectedQuestionnaireStore = useSelectedQuestionnaire();
const booleanAnswerDisplayText = selectedQuestionnaireStore.getCommonItems(
  QuestionnaireTemplateCommonItemsId.Boolean
);

const individualOwnerDisplayTextItems =
  selectedQuestionnaireStore.getCompanyOwnerDisplayTextItems();

const itemsStore = useItemsStore();
const companies: Ref<CompanyOwner[]> = ref(getCompanyOwners());
const question = computed(() => props.questionModel.question);
const showAnswer = computed(() => companyOwnershipAnswerComplete(props.answer));

function emitNewCompany(): void {
  const answerCompanies = companies.value.map((x) => x as AnswerCompanyOwner);
  emit("qEvent", answerCompanies);
}

function emitValid(isValid: boolean, fieldId: string): void {
  emit("isValid", isValid, `${question.value.id}__${fieldId}`);
}

function addCompany(): void {
  companies.value.push(createEmptyCompanyOwner());
}

function removeCompany(item: CompanyOwner): void {
  const index = companies.value.indexOf(item);
  companies.value.splice(index, 1);
  emitNewCompany();
}

function getCompanyOwners(): CompanyOwner[] {
  if (!props.answer || props.answer.length === 0) {
    return [createEmptyCompanyOwner()];
  }

  return props.answer.map((answerCompanyOwner: AnswerCompanyOwner) => {
    return answerCompanyOwner as CompanyOwner;
  });
}

function getBooleanDisplayText(answerId: string): string {
  const entry = booleanAnswerDisplayText.find((x) => x.id == answerId);
  return entry?.text ?? "";
}

function getLabel(id: string): string {
  return individualOwnerDisplayTextItems.find((x) => x.id == id)?.text ?? "";
}
</script>
